/**
 * AudioPlayer — EAA-1755.
 *
 * Branded, reusable audio player (replaces the raw native `<audio controls>`):
 * a pill bar with a circular play/pause button, current/total time, a seekable
 * progress slider, and a mute toggle. Self-contained audio logic so any
 * surface (recording playback, model audio, the audio element) can drop it in.
 * Theme-token styled; play button reuses the playful-skin-aware CircleIconButton.
 */

import * as React from 'react';
import { cn } from '../../lib/utils';
import { CircleIconButton } from './CircleIconButton';
import { AudioWaveform } from './AudioWaveform';
import { Popover, PopoverContent, PopoverTrigger } from '../ui';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';

export interface AudioPlayerProps {
    src: string;
    autoPlay?: boolean;
    loop?: boolean;
    className?: string;
    labels?: { play?: string; pause?: string; mute?: string; unmute?: string };
}

function formatTime(seconds: number): string {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Selectable playback rates, cycled by the speed button. */
const PLAYBACK_RATES = [0.75, 1, 1.25, 1.5, 2] as const;

export const AudioPlayer: React.FC<AudioPlayerProps> = ({
    src,
    autoPlay = false,
    loop = false,
    className,
    labels,
}) => {
    const audioRef = React.useRef<HTMLAudioElement | null>(null);
    const [playing, setPlaying] = React.useState(false);
    const [current, setCurrent] = React.useState(0);
    const [duration, setDuration] = React.useState(0);
    const [muted, setMuted] = React.useState(false);
    const [volume, setVolume] = React.useState(1);
    const [rate, setRate] = React.useState(1);

    // Keep the media element's volume/rate in sync with UI state (also re-applied
    // when a new element mounts, since playbackRate/volume reset on src change).
    React.useEffect(() => {
        const el = audioRef.current;
        if (!el) return;
        el.volume = volume;
        el.playbackRate = rate;
    }, [volume, rate, src]);

    const togglePlay = () => {
        const el = audioRef.current;
        if (!el) return;
        if (el.paused) void el.play();
        else el.pause();
    };

    const seekTo = (t: number) => {
        const el = audioRef.current;
        if (!el) return;
        el.currentTime = t;
        setCurrent(t);
    };

    const toggleMute = () => {
        const el = audioRef.current;
        if (!el) return;
        // Unmuting a zero volume would stay silent — restore to full.
        if (el.muted || volume === 0) {
            if (volume === 0) {
                setVolume(1);
                el.volume = 1;
            }
            el.muted = false;
            setMuted(false);
        } else {
            el.muted = true;
            setMuted(true);
        }
    };

    const handleVolume = (e: React.ChangeEvent<HTMLInputElement>) => {
        const el = audioRef.current;
        const v = Number(e.target.value);
        setVolume(v);
        setMuted(v === 0);
        if (el) {
            el.volume = v;
            el.muted = v === 0;
        }
    };

    const [speedOpen, setSpeedOpen] = React.useState(false);
    const selectRate = (r: number) => {
        setRate(r);
        if (audioRef.current) audioRef.current.playbackRate = r;
        setSpeedOpen(false);
    };

    const showMuted = muted || volume === 0;

    return (
        <div
            data-slot="audio-player"
            className={cn(
                'audio-player flex w-full max-w-md items-center gap-3 rounded-full border border-border bg-muted/40 px-3 py-1.5',
                className
            )}
        >
            <CircleIconButton
                variant="primary"
                size="sm"
                onClick={togglePlay}
                aria-label={playing ? labels?.pause : labels?.play}
                className="audio-player-play h-11 w-11 shrink-0" /* 44px touch target (was 36) */
            >
                {playing ? <Pause className="h-4 w-4" /> : <Play className="ml-0.5 h-4 w-4" />}
            </CircleIconButton>

            <span className="audio-player-time shrink-0 text-xs leading-none tabular-nums text-muted-foreground">
                {formatTime(current)} / {formatTime(duration)}
            </span>

            {/* Waveform sits in the centre, filling the space between the controls. */}
            <AudioWaveform
                src={src}
                duration={duration}
                current={current}
                onSeek={seekTo}
                className="audio-player-track min-w-0 flex-1"
            />

            <div className="audio-player-controls flex shrink-0 items-center gap-1">
                    {/* Volume — click opens a slider popover above (flips below if no room). */}
                    <Popover>
                        <PopoverTrigger asChild>
                            <button
                                type="button"
                                aria-label={showMuted ? labels?.unmute : labels?.mute}
                                className="audio-player-volume-trigger inline-flex min-h-11 min-w-11 items-center justify-center rounded-md p-1 text-muted-foreground transition-colors duration-200 hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                                {showMuted ? (
                                    <VolumeX className="h-5 w-5" />
                                ) : (
                                    <Volume2 className="h-5 w-5" />
                                )}
                            </button>
                        </PopoverTrigger>
                        <PopoverContent
                            side="top"
                            align="center"
                            sideOffset={8}
                            className="audio-player-volume-popover flex w-auto items-center gap-2 p-2"
                        >
                            <button
                                type="button"
                                onClick={toggleMute}
                                aria-label={showMuted ? labels?.unmute : labels?.mute}
                                className="text-muted-foreground transition-colors duration-200 hover:text-foreground"
                            >
                                {showMuted ? (
                                    <VolumeX className="h-4 w-4" />
                                ) : (
                                    <Volume2 className="h-4 w-4" />
                                )}
                            </button>
                            <input
                                type="range"
                                min={0}
                                max={1}
                                step={0.05}
                                value={showMuted ? 0 : volume}
                                onChange={handleVolume}
                                aria-label="Volume"
                                className="audio-player-volume-slider h-1 w-28 cursor-pointer accent-primary"
                            />
                        </PopoverContent>
                    </Popover>

                    {/* Playback speed — click opens a rate list popover above/below. */}
                    <Popover open={speedOpen} onOpenChange={setSpeedOpen}>
                        <PopoverTrigger asChild>
                            <button
                                type="button"
                                aria-label="Playback speed"
                                className="audio-player-speed inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-md px-1.5 py-0.5 text-xs font-semibold tabular-nums text-muted-foreground transition-colors duration-200 hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                                {rate}×
                            </button>
                        </PopoverTrigger>
                        <PopoverContent
                            side="top"
                            align="end"
                            sideOffset={8}
                            className="audio-player-speed-popover flex w-auto flex-col gap-0.5 p-1"
                        >
                            {PLAYBACK_RATES.map((r) => (
                                <button
                                    key={r}
                                    type="button"
                                    onClick={() => selectRate(r)}
                                    className={cn(
                                        'rounded px-3 py-1 text-left text-sm tabular-nums transition-colors duration-200 hover:bg-muted',
                                        r === rate ? 'bg-muted font-semibold text-foreground' : 'text-muted-foreground'
                                    )}
                                >
                                    {r}×
                                </button>
                            ))}
                        </PopoverContent>
                    </Popover>
            </div>

            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <audio
                ref={audioRef}
                src={src}
                autoPlay={autoPlay}
                loop={loop}
                preload="metadata"
                className="hidden"
                onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
                onDurationChange={(e) => setDuration(e.currentTarget.duration)}
                onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime)}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => setPlaying(false)}
            />
        </div>
    );
};

export default AudioPlayer;
